import { Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";

import { AuthShell } from "@/components/shared/AuthShell";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { fireRegistrationConversion } from "@/lib/google-ads";

export function SsoCallbackPage(): JSX.Element {
  const [searchParams] = useSearchParams();
  const code = searchParams.get("code");
  const error = searchParams.get("error");
  const isNew = searchParams.get("new") === "1";
  const { exchangeSsoCodeAndLogin } = useAuth();
  const { t } = useTranslation();
  const [status, setStatus] = useState<
    "pending" | "success" | "newUser" | "error" | "usePassword" | "noTenant"
  >("pending");
  const exchangedRef = useRef(false);

  useEffect(() => {
    if (error === "use_password") {
      setStatus("usePassword");
      return;
    }
    if (error === "no_tenant") {
      setStatus("noTenant");
      return;
    }
    if (error) {
      toast.error(t("auth.sso.error", "Sign-in failed. Please try again."));
      setStatus("error");
      return;
    }
    if (!code) {
      toast.error(t("auth.sso.error", "Sign-in failed. Please try again."));
      setStatus("error");
      return;
    }
    if (exchangedRef.current) return;
    exchangedRef.current = true;
    exchangeSsoCodeAndLogin(code)
      .then(() => {
        if (isNew) {
          // Fire the Google Ads "Sign Up" conversion for SSO-created accounts.
          // The fireRegistrationConversion helper is sessionStorage-de-duped so
          // double-fires (across guest-checkout vs standalone register pages) won't
          // double-count. Without this, Google SSO registrations from the wizard
          // were invisible to Ads optimization.
          fireRegistrationConversion();
          toast.success(t("auth.sso.welcome", "Welcome! Complete your registration."));
          setStatus("newUser");
        } else {
          toast.success(t("auth.sso.success", "Signed in successfully."));
          setStatus("success");
        }
      })
      .catch(() => {
        toast.error(t("auth.sso.error", "Sign-in failed. Please try again."));
        setStatus("error");
      });
  }, [code, error, isNew, exchangeSsoCodeAndLogin]);

  if (status === "success" || status === "newUser") {
    const returnTo = searchParams.get("returnTo");
    if (returnTo && returnTo.startsWith("/")) {
      return <Navigate to={returnTo} replace />;
    }
    // `newUser` no longer routes to the account-type picker (it's been
    // retired — every new consumer is auto-created as a `person`). The
    // `?new=1` flag still fired the Google Ads conversion above; from
    // here both success and newUser land on the home view so existing
    // ProtectedRoute gates (name entry, email verification) can take
    // over if anything's still missing.
    return <Navigate to="/" replace />;
  }

  if (status === "usePassword") {
    return (
      <AuthShell>
        <div className="flex flex-col items-center gap-4 text-center">
          <p className="text-muted-foreground">
            {t("auth.sso.usePassword", "Use email/password to sign in.")}
          </p>
          <Button asChild>
            <Link to="/login">
              {t("auth.sso.usePasswordLink", "Go to login")}
            </Link>
          </Button>
        </div>
      </AuthShell>
    );
  }

  if (status === "noTenant") {
    return (
      <AuthShell>
        <div className="flex flex-col items-center gap-4 text-center">
          <p className="text-muted-foreground">
            {t("auth.sso.noTenant", "No tenant assigned. Contact your administrator.")}
          </p>
          <Button asChild variant="outline">
            <Link to="/login">{t("pages.common.back", "Back")}</Link>
          </Button>
        </div>
      </AuthShell>
    );
  }

  if (status === "error") {
    return (
      <AuthShell>
        <div className="flex flex-col items-center gap-4 text-center">
          <p className="text-muted-foreground">
            {t("auth.sso.error", "Sign-in failed. Please try again.")}
          </p>
          <Button asChild variant="outline">
            <Link to="/login">{t("pages.common.back", "Back")}</Link>
          </Button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <div className="flex flex-col items-center gap-4 text-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden />
        <p className="text-muted-foreground">
          {t("auth.sso.completing", "Completing sign-in…")}
        </p>
      </div>
    </AuthShell>
  );
}
